import { Item, Section } from '@react-stately/collections'
import { DocumentIcon } from 'components/Icons/DocumentIcon'
import { type EditorView } from 'prosemirror-view'
import { useState, useRef, useEffect, Key } from 'react'
import Styles from './MentionsPlugin.module.scss'
import { Mentionable, SuggestionsSection } from './Types'
import { Selection } from '@react-types/shared'
import { List } from './List'
import Avatar from 'common/Avatar/Avatar'
import { MENTIONS_PLUGIN_KEY } from './constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useSearch } from './useSearch'

interface Props {
  /**
   * The editor view. This is used by the list to attach its keyboard handlers to the view dom
   */
  view: EditorView
  /**
   * The text of the mention
   */
  query: string
  /**
   * Called when the user selects a suggestion via either keyboard or pointer
   */
  onSelect: (mentionable: Mentionable) => void
}

export const Suggestions = ({ view, query, onSelect }: Props) => {
  const [results, setResults] = useState<Mentionable[]>([])
  const pluginState = MENTIONS_PLUGIN_KEY.getState(view.state)
  const workspaceId = useCurrentWorkspaceId()
  const search = useSearch({ workspaceId })
  const recordsCache = useRef(new Map<string, Mentionable>())
  const queryRef = useRef(query)

  const cacheRecords = (mentionables: Mentionable[]) => {
    mentionables.forEach((mentionable) => {
      const key = mentionable.mentionableType === 'User' ? mentionable.user_id.toString() : mentionable.key
      recordsCache.current.set(key, mentionable)
    })
  }

  useEffect(() => {
    queryRef.current = query
    const runSearch = async (query: string) => {
      const results = await search(query)
      cacheRecords(results)
      if (query === queryRef.current) {
        setResults(results)
      }
    }
    runSearch(query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  if (!pluginState?.active || results.length === 0) {
    return null
  }

  const handleSelect = (keys: Selection) => {
    const key = (keys as Set<Key>).values().next().value
    if (typeof key !== 'string') {
      return
    }
    const mentionable = recordsCache.current.get(key)
    if (mentionable) {
      onSelect(mentionable)
    }
  }

  const users = results.filter((r) => r.mentionableType === 'User')
  const articles = results.filter((r) => r.mentionableType === 'Article')
  const sections: SuggestionsSection[] = []
  if (users.length > 0) {
    sections.push({ name: 'Users', children: users })
  }
  if (articles.length > 0) {
    sections.push({ name: 'Docs', children: articles })
  }

  return (
    <div className={Styles.dropdown}>
      <List view={view} items={sections} onSelectionChange={handleSelect}>
        {(item) => (
          <Section key={item.name} items={item.children} title={item.name}>
            {(item) => {
              if (item.mentionableType === 'User') {
                return (
                  <Item textValue={item.full_name} key={item.user_id}>
                    <Avatar className={Styles.avatar} size={20} url={item.profile_image_url} />
                    <span className={Styles.title}>{item.full_name}</span>
                  </Item>
                )
              } else {
                return (
                  <Item textValue={item.title} key={item.key}>
                    {item.header_emoji ? (
                      <Avatar
                        className={Styles.avatar}
                        size={20}
                        emoji={item.header_emoji}
                        backgroundColor="transparent"
                      />
                    ) : (
                      <DocumentIcon className={Styles.avatar} />
                    )}
                    <span className={Styles.title}>{item.title}</span>
                  </Item>
                )
              }
            }}
          </Section>
        )}
      </List>
    </div>
  )
}
